import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Trophy, Crown, Medal, Star, Users, CheckCircle } from '@phosphor-icons/react';

const rankColor = (rank) => {
  if (rank === 1) return { bg: 'bg-amber-50 border-amber-300', icon: 'text-amber-500', medal: 'gold' };
  if (rank === 2) return { bg: 'bg-slate-50 border-slate-300', icon: 'text-slate-400', medal: 'silver' };
  if (rank === 3) return { bg: 'bg-orange-50 border-orange-300', icon: 'text-orange-400', medal: 'bronze' };
  return { bg: 'bg-white border-slate-200', icon: 'text-slate-400', medal: null };
};

const Leaderboard = () => {
  const navigate = useNavigate();
  const { API, language, user } = useAuth();
  const L = (en, deu) => (language === 'de' ? deu : en);
  const isStaff = ['admin', 'staff', 'trainer'].includes(user?.role);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('top'); // 'top' | 'club' (admin only)
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        setLoading(true);
        const url = viewMode === 'club' && isStaff
          ? '/leaderboard?full=true'
          : '/leaderboard?limit=20';
        const { data } = await API.get(url);
        if (!cancel) setRows(data.rows || []);
      } catch {
        if (!cancel) setRows([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [API, viewMode, isStaff]);

  const filteredRows = viewMode === 'club' && searchTerm
    ? rows.filter(r =>
        (r.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.email || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : rows;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leaderboard-page">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-md">
          <Trophy size={24} weight="fill" className="text-white" />
        </div>
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">{L('Padlr Leaderboard', 'Padlr Bestenliste')}</h1>
          <p className="text-slate-500 text-sm">
            {viewMode === 'club'
              ? L('Full club ranking (admin view)', 'Vollständige Club-Rangliste (Admin-Ansicht)')
              : L('Top players ranked by Padlr Rating', 'Top-Spieler nach Padlr Rating')}
          </p>
        </div>
      </div>

      {/* Admin view toggle */}
      {isStaff && (
        <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 w-fit" data-testid="leaderboard-view-toggle">
          <button
            onClick={() => setViewMode('top')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${viewMode === 'top' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="leaderboard-view-top"
          >
            <Trophy size={14} className="inline mr-1.5" /> {L('Top 20', 'Top 20')}
          </button>
          <button
            onClick={() => setViewMode('club')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${viewMode === 'club' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid="leaderboard-view-club"
          >
            <Users size={14} className="inline mr-1.5" /> {L('Full club', 'Ganzer Club')}
          </button>
        </div>
      )}

      {viewMode === 'club' && isStaff && (
        <input
          type="text"
          placeholder={L('Search by name or email…', 'Nach Name oder E-Mail suchen…')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
          data-testid="leaderboard-search"
        />
      )}

      {loading ? (
        <div className="space-y-2">{[1,2,3,4,5].map(i => <div key={i} className="h-14 bg-slate-100 animate-pulse rounded-xl" />)}</div>
      ) : filteredRows.length === 0 ? (
        <div className="card-static p-12 text-center">
          <Trophy size={48} className="text-slate-300 mx-auto mb-3" weight="duotone" />
          <p className="text-slate-500">
            {viewMode === 'club' && searchTerm
              ? L('No players match your search.', 'Keine Spieler passen zur Suche.')
              : L('No rated players yet — competitive matches and Mixins will populate the leaderboard.',
                  'Noch keine bewerteten Spieler — kompetitive Matches und Mixins füllen die Bestenliste.')}
          </p>
        </div>
      ) : (
        <>
          {viewMode === 'club' && isStaff && (
            <p className="text-xs text-slate-500" data-testid="leaderboard-count">
              {filteredRows.length} {L('players', 'Spieler')} · {filteredRows.filter(r => r.rated).length} {L('rated', 'bewertet')}
            </p>
          )}
          <div className="space-y-2" data-testid="leaderboard-list">
            {filteredRows.map(row => {
              const c = rankColor(row.rank);
              const isMe = row.user_id === user?.id;
              const clickable = isStaff && row.user_id;
              return (
                <div
                  key={row.user_id}
                  data-testid={`leaderboard-row-${row.rank}`}
                  onClick={() => clickable && navigate(`/admin/users/${row.user_id}`)}
                  className={`flex items-center gap-3 p-3 sm:p-4 rounded-xl border ${c.bg} ${isMe ? 'ring-2 ring-emerald-400' : ''} transition ${clickable ? 'cursor-pointer hover:shadow-md' : ''}`}
                >
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 rounded-lg flex items-center justify-center font-black text-lg ${c.bg}`}>
                    {row.rank <= 3 ? (
                      row.rank === 1 ? <Crown size={22} weight="fill" className={c.icon} /> : <Medal size={22} weight="fill" className={c.icon} />
                    ) : (
                      <span className="text-slate-500">{row.rank}</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-slate-900 truncate">
                        {row.name}
                        {isMe && <span className="ml-2 text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded uppercase">{L('You', 'Du')}</span>}
                      </p>
                      {row.is_member && <Star size={14} weight="fill" className="text-sky-500 flex-shrink-0" title={L('Member', 'Mitglied')} />}
                      {row.level_verified && !row.is_member && <CheckCircle size={14} weight="fill" className="text-[#22c55e] flex-shrink-0" title={L('Level verified by our coach', 'Level vom Coach verifiziert')} data-testid="leaderboard-level-verified" />}
                      {viewMode === 'club' && !row.rated && (
                        <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded uppercase">{L('unrated', 'unbewertet')}</span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 truncate">
                      {row.matches_played} {L('matches', 'Spiele')} · {row.matches_won} {L('wins', 'Siege')} ({row.win_rate}%) · L{row.player_level?.toFixed(2)}
                      {viewMode === 'club' && row.email && <span className="ml-2 text-slate-400">· {row.email}</span>}
                    </p>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className={`text-2xl font-black leading-none ${row.rated ? 'text-emerald-600' : 'text-slate-400'}`} data-testid={`leaderboard-rating-${row.rank}`}>{row.padlr_rating}</p>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">{L('Rating', 'Rating')}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default Leaderboard;
